// The week breakdown behind the Breakdown button on My Time.
//
// The summary bar gives four figures and the grid gives the entries; neither
// totals a single job across the week. This turns a period's entries into a
// matrix: a row per task, project and overhead category, a column per day, a
// total down the right and along the bottom.
//
// The grouping is deliberately identical to the summary bar's (task first, then
// SOW, then overhead), so the breakdown reconciles with the tiles directly above
// it on the same screen. Two figures on one screen that disagree are worse than
// one figure on its own. tests/my-time-week-breakdown.test.mjs pins both the
// grouping and the arithmetic.

// Quarter hours summed as floats leak 0.30000000000000004, and every figure
// here reaches the screen, so each one is rounded on the way out.
const round2 = (n) => Math.round(n * 100) / 100;

const GROUP_ORDER = [
  { key: 'tasks', label: 'Tasks' },
  { key: 'projects', label: 'Projects' },
  { key: 'overheads', label: 'Overheads' },
];

// Task beats SOW beats neither, matching byType in MyTimePage: a task logged
// against a SOW is task time, and bid/SOW-creation time (which carries both a
// sow_id and an overhead category) is project time.
function groupKeyFor(e) {
  if (e.task_id) return 'tasks';
  if (e.sow_id) return 'projects';
  return 'overheads';
}

function rowFor(e, groupKey) {
  if (groupKey === 'tasks') {
    return { key: `task:${e.task_id}`, label: e.task_name || 'Unnamed task', sublabel: '' };
  }
  if (groupKey === 'projects') {
    return { key: `sow:${e.sow_id}`, label: e.sow_reference || 'Unnamed project', sublabel: e.sow_detail || '' };
  }
  return {
    key: `overhead:${e.overhead_category_id || 'none'}`,
    label: e.overhead_category_name || 'Uncategorised',
    sublabel: '',
  };
}

export function buildBreakdown(entries, days) {
  const inPeriod = new Set(days || []);
  const buckets = new Map();   // groupKey -> Map(rowKey -> row)
  const byDay = {};
  let total = 0;

  for (const e of entries || []) {
    if (!inPeriod.has(e.date)) continue;
    const hours = parseFloat(e.hours);
    if (!Number.isFinite(hours)) continue;

    const groupKey = groupKeyFor(e);
    if (!buckets.has(groupKey)) buckets.set(groupKey, new Map());
    const rows = buckets.get(groupKey);

    const shape = rowFor(e, groupKey);
    if (!rows.has(shape.key)) rows.set(shape.key, { ...shape, byDay: {}, total: 0 });
    const row = rows.get(shape.key);

    row.byDay[e.date] = (row.byDay[e.date] || 0) + hours;
    row.total += hours;
    byDay[e.date] = (byDay[e.date] || 0) + hours;
    total += hours;
  }

  const groups = GROUP_ORDER
    .filter(g => buckets.has(g.key))
    .map(g => {
      const rows = [...buckets.get(g.key).values()]
        .map(r => ({
          ...r,
          byDay: Object.fromEntries(Object.entries(r.byDay).map(([d, h]) => [d, round2(h)])),
          total: round2(r.total),
        }))
        // Most time first, so the week's biggest commitment is the first thing
        // read. Ties fall back to the label so the order never wobbles between
        // renders of the same data.
        .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));
      return { ...g, rows, total: round2(rows.reduce((s, r) => s + r.total, 0)) };
    });

  return {
    days: days || [],
    groups,
    byDay: Object.fromEntries(Object.entries(byDay).map(([d, h]) => [d, round2(h)])),
    total: round2(total),
  };
}
